import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';

import { ReactComponent as AlertV1Icon } from '../../../assets/icons/alert-v1-icon.svg';
// import { ReactComponent as PaymentIcon } from '../../../assets/icons/amount-icon.svg';
import { ReactComponent as NavigateBack } from '../../../assets/icons/arrow-left.svg';
import { ReactComponent as ClockIcon } from '../../../assets/icons/clock-icon.svg';
import { ReactComponent as EnergyIcon } from '../../../assets/icons/energy-icon.svg';
import { ReactComponent as PlugIcon } from '../../../assets/icons/plug-icon.svg';
import { ReactComponent as RefreshIcon } from '../../../assets/icons/refresh-icon.svg';
import { ReactComponent as TimerIcon } from '../../../assets/icons/timer-icon.svg';
import { ReactComponent as UnplugIcon } from '../../../assets/icons/unplug-icon.svg';
import ChargerBg from '../../../assets/images/charger-bg.png';
import ConnectorUnavailable from '../../../assets/images/unavailable.png';
import { brandImage, useCurrencyText } from '../../../config/branding';
import config from '../../../config/config';
import { allStatus } from '../../../config/enum';
import { useBranding } from '../../../context/BrandingContext';
import { useWebSocket } from '../../../context/WebSocket/WebSocketProvider';
import BatteryLoader from '../../../shared-components/battery-loader/BatteryLoader';
import MyButton from '../../../shared-components/button/button';
import Footer from '../../../shared-components/Footer/Footer';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import ToastMessage from '../../../shared-components/toast-message/ToastMessage';
import getFirstSubdomain from '../../../shared-functions/DomiansplitFunction';
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from '../../../shared-functions/LocalStorage';
// import showNotification from '../../../shared-functions/Notification';
import charger from '../../../utils/api/charger';

import ChargingSpeedSocChart from './ChargingSpeedSocChart';

interface ConnectorInterface {
  _id: string;
  connector: string;
  connectorId: string | number;
  isReservationEnabled: boolean;
  status: string;
}

interface ChargingStatusProperties {
  paymentDetails: any;
  navigateToPaymentMethod: (removeQueryParameters?: boolean) => void;
  navigateToInvoice: () => void;
  chargingStatus: boolean;
  connectors: ConnectorInterface[];
  skipChargingButton: boolean;
  currentPrice: string;
  appVersion: string;
}

interface LiveCostWatts {
  liveCost: number;
  liveWatts: number;
  liveTime: {
    hours: number;
    minutes: number;
  };
}

interface DurationAmount {
  from: number;
  to: number;
  mode: 'grace' | 'flatRate' | 'perMinute';
  amount: number;
}

interface SocKwApiPoint {
  ts: string;
  kw: number;
  soc: number;
}
interface SocWsPayload {
  ts: string;
  kW?: number;
  kw?: number;
  soc: number;
}

interface ModalBoxDetail {
  title: string | null;
  content: JSX.Element | null;
  notificationModal: boolean;
}
const LS_WAITING_FOR_PLUG = 'waitingForPlug';
const LS_PLUG_START_TIME = 'plugStartTime';

/**
 *getElapsedMinutes
 */
const getElapsedMinutes = (startTime: string) => {
  const start = new Date(startTime).getTime();
  const now = Date.now();
  return Math.floor((now - start) / 60000); // minutes
};

/**
 * getEffectiveCeilingValue
 * @param ceilingType \
 * @param ceilingValue
 * @param totalAmount
 * @returns
 */
const getEffectiveCeilingValue = (
  ceilingType: 'fixedAmount' | 'percentage',
  ceilingValue: number,
  totalAmount: number,
) => {
  if (ceilingType === 'percentage') {
    // totalAmount = 0.2 → 20%
    return ceilingValue * totalAmount;
  }

  return ceilingValue; // fixedAmount
};

/**
 *getElapsedMinutes
 */
const calculateIdleFeeAmount = (
  elapsedMinutes: number,
  slabs: DurationAmount[],
  ceilingType: 'fixedAmount' | 'percentage',
  ceilingValue: number,
  totalAmount: number,
) => {
  const effectiveCeiling = getEffectiveCeilingValue(ceilingType, ceilingValue, totalAmount);

  let totalFee = 0;

  slabs.some((slab) => {
    if (elapsedMinutes < slab.from) {
      return true; // stop
    }

    // ✅ Flat Rate → applied once
    if (slab.mode === 'flatRate') {
      totalFee += slab.amount;
    }

    // ✅ Per Minute → starts counting immediately
    if (slab.mode === 'perMinute') {
      const maxMinutes = slab.to - slab.from + 1;
      const usedMinutes = Math.min(elapsedMinutes - slab.from + 1, maxMinutes);

      if (usedMinutes > 0) {
        totalFee += usedMinutes * slab.amount;
      }
    }

    // ✅ Ceiling
    if (totalFee >= effectiveCeiling) {
      totalFee = effectiveCeiling;
      return true;
    }

    return false;
  });

  return totalFee;
};

/**
 *getElapsedMinutes
 */
const getCurrentIdleMode = (elapsedMinutes: number, slabs: DurationAmount[]) => {
  const slab = slabs.find((item) => elapsedMinutes >= item.from && elapsedMinutes < item.to);
  return slab ? slab.mode : '';
};

/**
 * get Idle Mode Label
 * @param mode \
 * @returns
 */
const getIdleModeLabel = (mode: string, t: any) => {
  switch (mode) {
    case 'grace':
      return t('gracePeriod');
    case 'flatRate':
      return t('flatRate');
    case 'perMinute':
      return t('perMinute');
    default:
      return '';
  }
};

/**
 * Payment method component
 * @param properties
 * @returns
 */
function ChargingStatus(properties: ChargingStatusProperties) {
  const { t } = useTranslation('charger');
  const currency = useCurrencyText(t);
  const domain = getFirstSubdomain();
  const { brandData } = useBranding();
  const {
    paymentDetails,
    navigateToPaymentMethod,
    navigateToInvoice,
    chargingStatus,
    connectors,
    skipChargingButton,
    currentPrice,
    appVersion,
  } = properties;

  const [actionRequired, setActionRequired] = useState(false);
  const webSocket = useWebSocket();
  const [waitingForPlug, setWaitingForPlug] = useState(false);
  const currentChargingConnector = getLocalStorageItem('selectedConnector');
  const [startCharging, setStartCharging] = useState<boolean>(chargingStatus);
  const [transactionId, setTransactionId] = useState<string | null>(
    getLocalStorageItem('guestToken') || null,
  );

  const [loader, setLoader] = useState<boolean>(false);
  const [stopButtonLoader, setStopButtonLoader] = useState<boolean>(
    getLocalStorageItem('chargingStatus') === 'waitingForCharingCancel' || false,
  );
  const [selectedConnector, setSelectedConnector] = useState<ConnectorInterface | null>(null);
  const [selectedConnectorId, setSelectedConnectorId] = useState<string | number>('');
  const { deviceId } = useParams<{ deviceId: string }>(); // Extracts the ID from the URL

  // For hyperpay
  const location = useLocation();
  const queryParameters = new URLSearchParams(location.search);
  const hyperPayId = queryParameters.get('id');
  const domainParameter = queryParameters.get('domain');

  const [toastStatus, setToastStatus] = useState({
    openToast: false,
    showToastMessage: '',
  });

  // State to handle socket issues
  const [countdown, setCountdown] = useState<number>(60); // Countdown timer
  const [hasResponse, setHasResponse] = useState<boolean>(false); // WebSocket response flag
  const [showTimer, setShowTimer] = useState(false);
  const timerReference = useRef<NodeJS.Timeout | null>(null); // Reference for the

  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: null,
    notificationModal: true,
  });
  const paidAmount = Number(getLocalStorageItem('paidAmount') || 0);
  const totalKwhConsumer = Number(getLocalStorageItem('totalKwhConsumer') || 0);
  const [totalKwhConsumed, setTotalKwhConsumed] = useState<string>('0');
  const [liveCostWatts, setLiveCostWatts] = useState<LiveCostWatts>({
    liveCost: 0,
    liveWatts: 0,
    liveTime: {
      hours: 0,
      minutes: 0,
    },
  });

  const [refreshLoading, setRefreshLoading] = useState<boolean>(false);
  const [idleElapsedTime, setIdleElapsedTime] = useState('00:00:00');
  const [idleAmount, setIdleAmount] = useState(0);
  const [idleMode, setIdleMode] = useState('');
  const [ceilingValue, setCeilingValue] = useState<number>(0);
  const [durationAmountList, setDurationAmountList] = useState<DurationAmount[]>([]);
  const [penaltyTransactionStartTime, setPenaltyTransactionStartTime] = useState<string | null>(
    null,
  );
  const [ceilingType, setCeilingType] = useState<'fixedAmount' | 'percentage'>('fixedAmount');
  const [totalAmount, setTotalAmount] = useState<number>(0);

  // ✅ REF for real-time access inside timers
  const iswaitingForPlugReference = useRef(false);
  const [realTimeChargingData, setRealTimeChargingData] = useState<SocKwApiPoint[]>([]);

  /**
   * Show plug timeout modal
   */
  const showPlugTimeoutModal = () => {
    setModalBoxDetail({
      title: '',
      content: (
        <div className="modal-error-message">
          <div className="error-content">
            <span>
              <TimerIcon />
            </span>
            <p>{t('plugTimeoutMessage')}</p>
          </div>
          <MyButton
            label={t('startAgain')}
            buttonType="submit"
            onClickFunc={() => {
              setModalBoxStatus('close');
            }}
          />
        </div>
      ),
      notificationModal: true,
    });
  };

  /**
   * Render plug countdown UI
   */
  const renderPlugCountdown = (): JSX.Element => {
    return (
      <div className="modal-error-message plug-content">
        <div className="error-content">
          <span>
            <PlugIcon />
          </span>
        </div>

        <p className="charger-label">{t('connectYourCharger')}</p>
        <p className="almost-label">{t('almostThere')}</p>

        <div className="plug-icon">
          <TimerIcon />
          <p>00:{countdown < 10 ? `0${countdown}` : countdown}s</p>
        </div>

        <p className="plug-description">{t('connectWithinTime', { seconds: 60 })}</p>

        <p className="again-description">{t('startAgainIfNotConnected')}</p>
      </div>
    );
  };

  /**
   * remove Local Storage Item
   */
  const resetPlugWaitingState = () => {
    if (timerReference.current) {
      clearInterval(timerReference.current);
    }
    iswaitingForPlugReference.current = false;
    setWaitingForPlug(false);
    removeLocalStorageItem(LS_WAITING_FOR_PLUG);
    removeLocalStorageItem(LS_PLUG_START_TIME);
    setShowTimer(false);
  };
  /**
   * startPlugWaitTimer
   */
  const startPlugWaitTimer = () => {
    if (timerReference.current) {
      clearInterval(timerReference.current);
    }
    setCountdown(60);
    setShowTimer(true);

    timerReference.current = setInterval(() => {
      setCountdown((previous) => {
        if (previous < 1) {
          if (timerReference.current) {
            clearInterval(timerReference.current);
          }

          setShowTimer(false);
          setLoader(false);

          // ✅ USE REF (NOT STATE)
          if (iswaitingForPlugReference.current) {
            iswaitingForPlugReference.current = false;
            setWaitingForPlug(false);

            // 🔁 force modal re-render safely
            setModalBoxStatus('close');
            setShowTimer(false);
            setTimeout(() => {
              setModalBoxStatus('open');
              showPlugTimeoutModal();
            }, 0);
          }

          return 0;
        }

        return previous - 1;
      });
    }, 1000);
  };

  /**
   * Function to start and stop countdown for sockets
   */
  const startCountdown = () => {
    if (timerReference.current) {
      clearInterval(timerReference.current); // Clear any existing timer
    }

    setCountdown(60);
    setShowTimer(true);

    timerReference.current = setInterval(() => {
      setCountdown((previous) => {
        if (previous < 1) {
          if (timerReference.current) {
            clearInterval(timerReference.current);
          }
          if (!hasResponse) {
            setLoader(false);
            setShowTimer(false);
            removeLocalStorageItem('guestToken');
            removeLocalStorageItem('selectedConnector');
            setModalBoxStatus('open');
            removeLocalStorageItem('chargingStatus');
            setModalBoxDetail({
              title: '',
              content: (
                <div className="modal-error-message">
                  <div className="error-content">
                    <span>
                      <TimerIcon />
                    </span>
                    <p>{t('timeout')}</p>
                  </div>
                  {/* <p className="description">Contact customer support!</p> */}
                  <MyButton
                    label={t('chargeAgain')}
                    buttonType="submit"
                    onClickFunc={() => navigateToPaymentMethod(true)}
                  />
                </div>
              ),
              notificationModal: true,
            });
          }
          return 0;
        }
        return previous - 1;
      });
    }, 1000);
  };

  /**
   * Function to enable charging loader and call start charging API
   */
  const handleStartCharging = async () => {
    if (selectedConnectorId) {
      const singleConnector: ConnectorInterface | null =
        connectors.find((connector) => connector.connectorId === selectedConnectorId) || null;

      if (singleConnector) {
        setLoader(true);

        // Integrate start Charging API
        let request: any = {
          chargingPointSn: deviceId,
          connectorId: singleConnector.connectorId,
        };

        if (
          !skipChargingButton &&
          (domain && brandData?.guestPaymentMethod
            ? brandData?.guestPaymentMethod
            : config.guestPaymentMode) === 'stripe'
        ) {
          request = {
            ...request,
            ...paymentDetails,
          };
        }

        if (
          !skipChargingButton &&
          (domain && brandData?.guestPaymentMethod
            ? brandData?.guestPaymentMethod
            : config.guestPaymentMode) === 'hyperpay'
        ) {
          request = {
            ...request,
            hyperpayCheckoutId: hyperPayId,
            paymentMethod: getLocalStorageItem('paymentMethod'),
          };
        }

        try {
          const response = await charger.startCharging(request);
          window.history.replaceState(
            {},
            '',
            domainParameter
              ? `${window.location.pathname}?domain=${domainParameter}`
              : window.location.pathname,
          );
          setSelectedConnector(singleConnector);
          if (response?.data?.connectorStatus === 'Available') {
            setWaitingForPlug(true);
            iswaitingForPlugReference.current = true;

            setLocalStorageItem(LS_WAITING_FOR_PLUG, 'true');
            setLocalStorageItem(LS_PLUG_START_TIME, Date.now().toString());
            setModalBoxStatus('open');
            setModalBoxDetail({
              title: '',
              content: renderPlugCountdown(),
              notificationModal: true,
            });
            startPlugWaitTimer();
          } else {
            startCountdown();
          }
        } catch (error: any) {
          setLoader(false);
          window.history.replaceState(
            {},
            '',
            domainParameter
              ? `${window.location.pathname}?domain=${domainParameter}`
              : window.location.pathname,
          );
          setToastStatus({
            openToast: true,
            showToastMessage: error?.response?.data?.message || t('somethingWrong'),
          });

          // Clear an existing timer
          setShowTimer(false);
          if (timerReference.current) {
            clearInterval(timerReference.current);
          }
        }
      }
    } else {
      setToastStatus({
        openToast: true,
        showToastMessage: t('selectConnector'),
      });
    }
  };

  /**
   * Function to handle unexpected stop charging
   */
  const handleUnexpectedStop = () => {
    setStopButtonLoader(false);
    removeLocalStorageItem('guestToken');
    removeLocalStorageItem('selectedConnector');
    // Show error popup and redirect the user to payment screen
    setModalBoxStatus('open');
    setModalBoxDetail({
      title: '',
      content: (
        <div className="modal-error-message">
          <div className="error-content">
            <p>{t('unexpectedError')}</p>
          </div>
          {/* <p className="description">Contact customer support!</p> */}
          <MyButton
            label={t('chargeAgain')}
            buttonType="submit"
            onClickFunc={() => navigateToPaymentMethod(true)}
          />
        </div>
      ),
      notificationModal: true,
    });
  };

  /**
   * Function to handle stop charging with network retry logic
   */
  const handleStopCharging = async (): Promise<void> => {
    setStopButtonLoader(true);
    let retryCount = 0;
    const maxRetries = 3;
    const retryDelay = 3000; // milliseconds

    /**
     * Stop charging attempt with retry logic
     */
    const attemptStop = async (): Promise<void> => {
      try {
        await charger.stopCharging({ chargingPointSn: deviceId });
        setLocalStorageItem('chargingStatus', 'waitingForCharingCancel');
        startCountdown();
        setStopButtonLoader(false);
      } catch (error: any) {
        const isNetworkError =
          !error.response &&
          (error.code === 'ECONNABORTED' ||
            error.code === 'ERR_NETWORK' ||
            error.message === 'Network Error' ||
            error.message?.includes('Failed to fetch'));

        if (isNetworkError && retryCount < maxRetries) {
          retryCount += 1;
          console.warn(
            `Network issue detected. Retrying stopCharging (${retryCount}/${maxRetries})...`,
          );

          // Fixed: Remove the return from the Promise executor
          await new Promise<void>((resolve) => {
            setTimeout(resolve, retryDelay);
          }); // No return value from the executor

          await attemptStop(); // await instead of return for consistent-return
          return;
        }

        if (isNetworkError && retryCount >= maxRetries) {
          console.error('Network issue persists after retries.');
          setStopButtonLoader(false);
          setToastStatus({
            openToast: true,
            showToastMessage: t('networkStopError'),
          });
          return;
        }

        console.error('Stop charging failed due to logic issue:', error);
        handleUnexpectedStop();

        // Clear timer safely
        setShowTimer(false);
        if (timerReference.current) {
          clearInterval(timerReference.current);
        }
        setStopButtonLoader(false);
      }
    };

    await attemptStop();
  };

  /**
   *open Stop Charging Confirm Modal
   */
  const openStopChargingConfirmModal = () => {
    setModalBoxDetail({
      title: null,
      notificationModal: true,
      content: (
        <div className="leave-confirm-modal">
          <p className="title">⚡ Leaving So Soon? ⚡</p>
          <p className="desc">Don’t shoot your charge — stay a bit longer.</p>

          <div className="button-group">
            <MyButton
              label="Stop Charging"
              buttonType="active-outline"
              onClickFunc={() => {
                setModalBoxStatus('close');
                handleStopCharging();
              }}
              loading={stopButtonLoader}
              disableButton={stopButtonLoader || showTimer}
            />

            <MyButton
              label="Stay With Us"
              buttonType="submit"
              onClickFunc={() => setModalBoxStatus('close')}
            />
          </div>
        </div>
      ),
    });

    setModalBoxStatus('open');
  };

  /**
   * Function that after real time charging started
   */
  const handleChargingStarted = () => {
    setStartCharging(true);
    setLoader(false);

    // No need to handle else condition, because there will always a connector before start charging
    if (selectedConnector) {
      setLocalStorageItem('selectedConnector', selectedConnector);
    }

    // Clear an existing timer
    setShowTimer(false);
    setHasResponse(false);

    if (timerReference.current) {
      clearInterval(timerReference.current);
    }

    setModalBoxStatus('close');
  };

  /**
   * Function to handle real time stop charging
   */
  const handleChargingStop = (socketMessage: any) => {
    setStopButtonLoader(false);
    removeLocalStorageItem('selectedConnector');
    removeLocalStorageItem('guestToken');
    removeLocalStorageItem('chargingStatus');
    setLocalStorageItem('totalAmount', socketMessage?.paidAmount || 0);
    setLocalStorageItem('powerUtilizationAmount', socketMessage?.powerUtilizationAmount || 0);
    setLocalStorageItem('totalKwhConsumer', socketMessage?.powerConsumed || 0);
    setLocalStorageItem('liveCost', socketMessage?.liveCost || 0);
    setLocalStorageItem('liveHours', socketMessage?.sessionTime?.hours || 0);
    setLocalStorageItem('liveMinutes', socketMessage?.sessionTime?.minutes || 0);
    setLocalStorageItem('penaltyAmount', socketMessage?.penaltyAmount || 0);
    setLocalStorageItem('sessionFee', socketMessage?.sessionFee || 0);
    setLocalStorageItem('platformFee', socketMessage?.platformFee || 0);
    navigateToInvoice();
  };

  /**
   * Function to handle error messages from web socket
   * @param socketMessage
   */
  const handleErrorFromSocket = (socketMessage: any) => {
    setLoader(false);
    setToastStatus({
      openToast: true,
      showToastMessage: socketMessage?.error?.message || t('somethingWentWrong'),
    });
    setSelectedConnector(null);
    removeLocalStorageItem('selectedConnector');

    // Clear an existing timer
    setShowTimer(false);
    setHasResponse(true);
    if (timerReference.current) {
      clearInterval(timerReference.current);
    }
  };

  /**
   * Function to handle refresh charging session
   */
  const handlePageRefresh = async () => {
    // Only refresh session once charging starts
    if (startCharging) {
      setRefreshLoading(true);
      try {
        // By default reconnect web-sokcet
        webSocket?.reconnectWebSocket();
        const { data: chargingTransaction } = await charger.refreshSession({
          transactionId,
          chargingPointSn: deviceId,
        });

        // If already finished, directly navigate to invoice page
        if (chargingTransaction?.status === allStatus.FINISHED) {
          handleChargingStop({
            paidAmount: Number(chargingTransaction?.paidAmount || '0'),
            powerConsumed: chargingTransaction?.powerConsumedInKwh || '0',
          });
        } else {
          // Handle current transaction status inPROGRESS - enable stop button
          setStopButtonLoader(false);
        }
      } catch (error: any) {
        setStopButtonLoader(false);
      }
      setRefreshLoading(false);
    }
  };

  /**
   * stop Count down
   */
  const stopCountdown = () => {
    setShowTimer(false);
    setHasResponse(true); // IMPORTANT: prevents timeout modal
    if (timerReference.current) {
      clearInterval(timerReference.current);
      timerReference.current = null;
    }
  };

  /**
   * waitingPlugOutStatus
   */
  const waitingPlugOutStatus = (socketMessage: any) => {
    stopCountdown();
    setActionRequired(true);
    // removeLocalStorageItem('chargingStatus');
    setLocalStorageItem('paidAmount', socketMessage?.paidAmount || 0);
    setLocalStorageItem('totalKwhConsumer', socketMessage?.powerConsumed || 0);
    setLocalStorageItem('liveCost', socketMessage?.liveCost || 0);
    setLocalStorageItem('liveHours', socketMessage?.sessionTime?.hours || 0);
    setLocalStorageItem('liveMinutes', socketMessage?.sessionTime?.minutes || 0);
  };

  /**
   * Merge new SOC point safely (avoid duplicates)
   */
  const appendSocPoint = useCallback((point: SocWsPayload) => {
    const formatted: SocKwApiPoint = {
      ts: point.ts,
      kw: point.kW ?? point.kw ?? 0,
      soc: point.soc,
    };

    setRealTimeChargingData((previous) => {
      if (previous.some((p) => p.ts === formatted.ts)) return previous;
      return [...previous, formatted];
    });
  }, []);

  /**
   * Populate edit form when entering edit mode
   */
  const handleActiveSession = async () => {
    try {
      const response = await charger.refreshSession({ transactionId, chargingPointSn: deviceId });
      const session = response?.data || null;
      if (session?.socVsKW?.length) {
        const history: SocKwApiPoint[] = session.socVsKW.map((p: any) => ({
          ts: p.ts,
          kw: p.kw ?? p.kW ?? 0,
          soc: p.soc,
        }));
        setRealTimeChargingData(history);
      }
    } catch (error: any) {
      console.error(t('selectConnector'));
    }
  };

  useEffect(() => {
    if (deviceId && transactionId) {
      setTransactionId(transactionId);
      handleActiveSession();
    }
  }, [deviceId, transactionId]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (currentChargingConnector?.connectorId && transactionId) {
      // setStartCharging(true);
      setSelectedConnector(currentChargingConnector);
      setTransactionId(transactionId);

      setToastStatus({
        openToast: true,
        showToastMessage: t('chargingAlreadyActive'),
      });
    }
  }, []);

  useEffect(() => {
    if (webSocket) {
      /**
       * Handle all the messages from web socket listener
       * @param socketMessage
       */
      const handleMessage = (socketMessage: any) => {
        if (
          socketMessage?.event === 'RealTime Charging Monitoring' &&
          socketMessage?.status === 'Charging started'
        ) {
          handleChargingStarted();
          resetPlugWaitingState();
          // showNotification(t('chargingStartedTitle'), t('chargingStartedMessage'));
        }

        if (
          socketMessage?.event === 'RealTime Charging Monitoring' &&
          socketMessage?.status === 'Charging Completed'
        ) {
          handleChargingStop(socketMessage);
          // showNotification(t('chargingCompletedTitle'), t('chargingCompletedMessage'));
          if (socketMessage?.socVsKW) appendSocPoint(socketMessage?.socVsKW);
        }
        if (socketMessage?.errorIn === 'rmt-start-transaction') {
          handleErrorFromSocket(socketMessage);
        }
        if (
          socketMessage?.event === 'RealTime Charging Monitoring' &&
          socketMessage?.status === 'Charging inprogress'
        ) {
          setTotalKwhConsumed(socketMessage?.totalKwhConsumed);
          // showNotification(t('chargingInProgressTitle'), t('chargingInProgressMessage'));
          setLiveCostWatts((previous) => ({
            ...previous,
            liveCost: socketMessage?.liveCost,
            liveWatts: socketMessage?.totalKwhConsumed,
            liveTime: {
              hours: socketMessage?.sessionTime?.hourse,
              minutes: socketMessage?.sessionTime?.minutes,
            },
          }));
          if (socketMessage?.socVsKW) appendSocPoint(socketMessage?.socVsKW);
        }
        if (
          socketMessage?.event === 'RealTime Charging Monitoring' &&
          socketMessage?.status === 'Waiting for connector plug out'
        ) {
          waitingPlugOutStatus(socketMessage);
          setCeilingValue(socketMessage?.ceilingValue || 0);
          setDurationAmountList(socketMessage?.durationAmountList || []);
          setPenaltyTransactionStartTime(
            socketMessage?.penaltyTransactionStartTime || new Date().toISOString(),
          );
          setCeilingType(socketMessage?.ceilingType || 'fixedAmount');
          setTotalAmount(socketMessage?.totalAmount || 0);
          setLocalStorageItem('idleState', {
            penaltyTransactionStartTime:
              socketMessage?.penaltyTransactionStartTime || new Date().toISOString(),
            durationAmountList: socketMessage?.durationAmountList || [],
            ceilingType: socketMessage?.ceilingType || 'fixedAmount',
            ceilingValue: socketMessage?.ceilingValue || 0,
            totalAmount: socketMessage?.totalAmount || 0,
          });
        }
      };

      webSocket.webSocketService.subscribe(handleMessage);

      return () => {
        webSocket.webSocketService.unsubscribe(handleMessage);
      };
    }

    // Explicitly return undefined if webSocket is falsy
    return undefined;
  }, [webSocket, selectedConnector]);

  useEffect(() => {
    const savedIdle = getLocalStorageItem('idleState');
    if (!savedIdle) return;

    setActionRequired(true);
    setPenaltyTransactionStartTime(savedIdle.penaltyTransactionStartTime);
    setDurationAmountList(savedIdle.durationAmountList);
    setCeilingType(savedIdle.ceilingType);
    setCeilingValue(savedIdle.ceilingValue);
    setTotalAmount(savedIdle.totalAmount);
  }, []);

  // Show an alert message while user tries to refresh the page
  useEffect(() => {
    /**
     * Function to handle component refresh
     * @param event
     */
    const handleBeforeUnload = async (event: BeforeUnloadEvent) => {
      // The message set here may not always be shown, depending on the browser.
      if (loader && !startCharging) {
        event.preventDefault();
        // Required for most modern browsers to show a confirmation dialog
        // eslint-disable-next-line no-param-reassign
        event.returnValue = t('leaveWarning');
      } else {
        // eslint-disable-next-line no-useless-return
        return;
      }
    };

    // Add the event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    // window.addEventListener('pagehide', handlePageHide);

    // Clean up the event listeners on component unmount
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // window.removeEventListener('pagehide', handlePageHide);
    };
  }, [loader, startCharging]);

  useEffect(() => {
    if (
      (selectedConnectorId === '' ||
        selectedConnectorId === null ||
        selectedConnectorId === undefined) &&
      connectors.length
    ) {
      setSelectedConnectorId(connectors[0].connectorId);
    }
  }, [selectedConnectorId, connectors]);

  useEffect(() => {
    if (modalBoxStatus === 'open' && iswaitingForPlugReference.current) {
      setModalBoxDetail((previous) => ({
        ...previous,
        content: renderPlugCountdown(),
      }));
    }
  }, [countdown]);

  useEffect(() => {
    const isWaiting = getLocalStorageItem(LS_WAITING_FOR_PLUG);
    const startTime = getLocalStorageItem(LS_PLUG_START_TIME);

    if (isWaiting === 'true' && startTime) {
      const elapsedSeconds = Math.floor((Date.now() - Number(startTime)) / 1000);
      const remaining = Math.max(60 - elapsedSeconds, 0);

      if (remaining > 0) {
        setWaitingForPlug(true);
        iswaitingForPlugReference.current = true;

        setCountdown(remaining);
        setShowTimer(true);

        setModalBoxStatus('open');

        // ✅ restart timer with remaining seconds
        if (timerReference.current) {
          clearInterval(timerReference.current);
        }

        timerReference.current = setInterval(() => {
          setCountdown((previous) => {
            if (previous <= 1) {
              setModalBoxStatus('open');
              resetPlugWaitingState();
              showPlugTimeoutModal();

              return 0;
            }
            return previous - 1;
          });
        }, 1000);
      }
    }
  }, []);

  // Keep ref in sync with state
  useEffect(() => {
    iswaitingForPlugReference.current = waitingForPlug;
  }, [waitingForPlug]);

  useEffect(() => {
    if (!actionRequired || !penaltyTransactionStartTime) {
      return undefined;
    }

    const interval = setInterval(() => {
      const elapsedMs = Date.now() - new Date(penaltyTransactionStartTime).getTime();

      const hours = Math.floor(elapsedMs / 3600000);
      const minutes = Math.floor((elapsedMs % 3600000) / 60000);
      const seconds = Math.floor((elapsedMs % 60000) / 1000);

      setIdleElapsedTime(
        `${String(hours).padStart(2, '0')}:${String(minutes).padStart(
          2,
          '0',
        )}:${String(seconds).padStart(2, '0')}`,
      );
      const elapsedMinutes = getElapsedMinutes(penaltyTransactionStartTime);
      setIdleAmount(
        calculateIdleFeeAmount(
          elapsedMinutes,
          durationAmountList,
          ceilingType,
          ceilingValue,
          totalAmount,
        ),
      );
      setIdleMode(getCurrentIdleMode(elapsedMinutes, durationAmountList));
    }, 1000);

    return () => clearInterval(interval);
  }, [actionRequired, penaltyTransactionStartTime, durationAmountList, ceilingValue]);

  const allowFreeCharging =
    skipChargingButton ||
    (domain && brandData?.guestPaymentMethod
      ? brandData?.guestPaymentMethod
      : config.guestPaymentMode) === 'free';
  return (
    <div className="charging-status-wrap">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        modalBoxAnimation="modalAnimateToLeft"
        notification={modalBoxDetail.notificationModal}
        header={!modalBoxDetail.notificationModal}
      />
      <ToastMessage
        open={toastStatus.openToast}
        onClose={() =>
          setToastStatus({
            openToast: false,
            showToastMessage: '',
          })
        }
        type="error"
        message={toastStatus.showToastMessage || t('somethingWentWrong')}
        onCloseTimer={6000}
      />
      <div className={`charging-status-container ${actionRequired ? '' : 'unplug'}`}>
        <div className="charging-status-title-wrap">
          <span
            role="presentation"
            onClick={() => {
              if (startCharging) return;
              navigateToPaymentMethod(true);
            }}>
            {!allowFreeCharging && <NavigateBack />}
          </span>
          <h1>{t('chargingSession')}</h1>
          {startCharging ? (
            <div className="refresh-charge-session">
              <span
                className={refreshLoading ? 'refresh-icon' : ''}
                role="presentation"
                onClick={() => handlePageRefresh()}>
                <RefreshIcon />
              </span>
            </div>
          ) : (
            <div />
          )}
        </div>
        {!actionRequired && (
          <>
            <div className="connectors-wrap">
              {selectedConnector && startCharging ? (
                <div className="each-connector-wrap" role="presentation">
                  <div className="each-connector-background" />
                  <div className="each-connector-container">
                    <img
                      src={brandImage.connectorAvailable}
                      alt=""
                      className="connector-status-image"
                    />
                    <div className="connector-details">
                      <h3>{selectedConnector.connector}</h3>
                      <p className="status available"> {t('charging')} </p>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {connectors.map((connector) => (
                    <div
                      className={`each-connector-wrap ${connector.connectorId === selectedConnectorId && 'selected-connector'}`}
                      role="presentation"
                      onClick={() => setSelectedConnectorId(connector.connectorId)}>
                      <div className="each-connector-background" />
                      <div className="each-connector-container">
                        <img
                          src={
                            connector?.status === 'Available'
                              ? brandImage.connectorAvailable
                              : ConnectorUnavailable
                          }
                          alt=""
                          className="connector-status-image"
                        />
                        <div className="connector-details">
                          <h3>{connector?.connector}</h3>
                          <p
                            className={`status ${connector?.status === 'Available' ? 'available' : 'unavailable'}`}>
                            {connector?.status}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
            <div className="charging-loader-wrap">
              <div className="charging-battery">
                <BatteryLoader loading={startCharging} />
              </div>
              <img src={ChargerBg} alt="" className="charger-bg-image" />
            </div>
          </>
        )}
        {actionRequired && (
          <div className="charging-status-container-unplug">
            {' '}
            <div className="idle-fee-card">
              <div className="idle-fee-alert">
                <div className="title">
                  <AlertV1Icon />
                  {t('idleFeeActive')}
                </div>
                <p className="desc">{t('idleFeeActiveDesc')} </p>
              </div>

              <div className="action-card">
                <div className="icon">
                  <UnplugIcon />
                </div>

                <p className="action-title">{t('actionRequired')}</p>
                <h3>{t('unplugConnector')}</h3>
                <p className="action-desc">{t('unplugDesc')} </p>

                <div className="charge-info">
                  <p className="ammount">
                    {t('chargedAmount')} {idleAmount} {currency}
                  </p>

                  <div className="flate-rate-content">
                    <p>{getIdleModeLabel(idleMode, t)}</p>

                    <p className="time">{idleElapsedTime}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {liveCostWatts && (
        <div className="live-info">
          <div className="live-info__item">
            <p className="label">{t('liveWatts')}</p>
            <p className="value">
              <EnergyIcon className="icon" />
              <span>
                {' '}
                {liveCostWatts.liveWatts ? liveCostWatts.liveWatts.toFixed(2) : totalKwhConsumer} kW
              </span>
            </p>
          </div>

          <div className="live-info__item">
            <p className="label">{t('liveCost')}</p>
            <p className="value">
              <span>
                {liveCostWatts.liveCost > 0 ? liveCostWatts.liveCost.toFixed(2) : paidAmount}{' '}
                {currency}
              </span>
            </p>
          </div>

          <div className="live-info__item">
            <p className="label">{t('elapsedTime')}</p>
            <p className="value">
              <ClockIcon className="icon" />
              <span>
                {liveCostWatts.liveTime.hours || 0}:{liveCostWatts.liveTime.minutes || 0}
              </span>
            </p>
          </div>
        </div>
      )}

      <div className="latest-info">
        {currentPrice ? (
          <div className="current-pricing-wrap">
            <div className="current-pricing-container">
              <div className="pricing-content">
                <p>{t('currentPrice')}</p>
                <h6>
                  {Number(currentPrice).toFixed(2)} {currency} /Kwh
                </h6>
              </div>
            </div>
          </div>
        ) : null}
        <div className="current-pricing-wrap">
          <div className="current-pricing-container">
            <div className="pricing-icon">
              <span>
                <EnergyIcon />
              </span>
            </div>
            <div className="pricing-content">
              <p>{t('energyConsumed')}</p>
              <h6>{totalKwhConsumed} Kwh</h6>
            </div>
          </div>
        </div>
      </div>
      <div className="charging-speed-soc-graph">
        <ChargingSpeedSocChart data={realTimeChargingData} available />
      </div>
      {showTimer && (
        <div className="countdown-wrap">
          <div className="countdown-container">
            <span>
              <TimerIcon />
            </span>
            <p>00:{countdown < 10 ? `0${countdown}` : countdown}</p>
          </div>
        </div>
      )}
      <div className="button-desc-wrap">
        {!actionRequired &&
          (!startCharging ? (
            <MyButton
              label={t('startCharging')}
              buttonType="submit"
              onClickFunc={handleStartCharging}
              loading={loader}
              disableButton={loader || !selectedConnectorId}
            />
          ) : (
            <MyButton
              label={t('stopCharging')}
              buttonType="error"
              onClickFunc={openStopChargingConfirmModal}
            />
          ))}
        {startCharging && <p className="info-text">{t('refreshInfo')}</p>}
        <div className={brandData?.footer ? 'app-version' : 'app-version-only'}>
          {brandData?.footer && <Footer />}
          <span>{appVersion}</span>
        </div>
      </div>
    </div>
  );
}

export default ChargingStatus;
